import { ClaimResponse } from '@sentara-health/sentara-api-hooks-core';
import { SortField } from 'common';
import { ClaimsFilterState } from '../types/claimsFilters';
import { formatMedicalText } from './TextFormater';

/**
 * Extracts normalized location from a claim.
 * Uses practiceName, falls back to payToProviderName, and applies formatMedicalText.
 */
export const getNormalizedClaimLocation = (claim: any): string => {
  const location =
    claim?.extension?.find((ext: any) => ext.url === 'practiceName')?.value ??
    claim?.extension?.find((ext: any) => ext.url === 'payToProviderName')?.value ??
    '';
  return location ? formatMedicalText(location) : '';
};

/**
 * Extracts normalized provider name from a claim.
 * Uses providerName and applies formatMedicalText.
 */
export const getNormalizedClaimProvider = (claim: any): string => {
  const provider =
    claim?.extension?.find((ext: any) => ext.url === 'providerName')?.value ??
    '';
  return provider ? formatMedicalText(provider) : '';
};

/**
 * Normalizes filter selections by removing falsy values and applying consistent formatting.
 * Returns empty array if no valid selections remain.
 * Applies formatMedicalText() and toLowerCase() for case-insensitive matching.
 */
export const normalizeFilterSelections = (
  selections?: string[]
): string[] => {
  if (!selections) return [];

  return selections
    .filter(Boolean)
    .map((item) => formatMedicalText(item).toLowerCase());
};

export const getServiceTypeParts = (claim: any): string[] => {
  let codings: any[] = [];
  if (claim?.type?.coding) {
    codings = Array.isArray(claim.type.coding)
      ? claim.type.coding
      : [claim.type.coding];
  }
  const display = codings
    .map((c: any) => c?.display as string | undefined)
    .filter(Boolean)
    .join(' | ');

  const raw = (display || '')
    .split(/[,|/;]+/)
    .map((p: string) => p.trim())
    .filter((p: string) => p.length > 0);

  const seen = new Set<string>();
  const parts: string[] = [];

  for (const p of raw) {
    const key = p.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      parts.push(p);
    }
  }
  return parts;
};

export const hasActiveFilters = (filters: ClaimsFilterState): boolean => {
  return !!(
    filters.selectedStatus?.length ||
    filters.selectedProviders?.length ||
    filters.selectedServiceTypes?.length ||
    filters.selectedLocations?.length ||
    filters.amountMin ||
    filters.amountMax
  );
};

export const applyFiltersToQueriedClaims = (
  queriedClaims: ClaimResponse[],
  appliedFilters: ClaimsFilterState
): ClaimResponse[] => {
  if (!hasActiveFilters(appliedFilters)) return queriedClaims;

  // Normalize filter
  const selectedStatuses = (appliedFilters.selectedStatus || []).map(s => s.toLowerCase());
  const selectedProviders = normalizeFilterSelections(appliedFilters.selectedProviders);
  const selectedLocations = normalizeFilterSelections(appliedFilters.selectedLocations);
  const selectedTypes = (appliedFilters.selectedServiceTypes || [])
    .filter((s) => s && s.toLowerCase() !== 'all')
    .map((s) => s.toLowerCase());

  return queriedClaims.filter((c) => {
    const claimStatus = (c.status ?? '').toLowerCase();
    const statusOk =
      !selectedStatuses.length ||
      selectedStatuses.includes(claimStatus);

    const claimProviderName = getNormalizedClaimProvider(c).toLowerCase();
    const providerOk =
      !selectedProviders.length ||
      (claimProviderName && selectedProviders.includes(claimProviderName));

    // Service type filter - unchanged logic
    const claimTypes = new Set<string>(
      getServiceTypeParts(c).map((p) => p.toLowerCase())
    );

    const serviceTypeOk =
      !selectedTypes.length || selectedTypes.some((s) => claimTypes.has(s));
    
    // Location filter - use normalized extraction matching UI logic
    const claimLocation = getNormalizedClaimLocation(c).toLowerCase();
    const locationOk =
      !selectedLocations.length ||
      (claimLocation && selectedLocations.includes(claimLocation));

    const numFromAmount = (val: string) => {
      const match = /\d+(?:\.\d+)?/.exec(val || '');
      return Number.parseFloat(match?.[0] || '0');
    };

    const amountValue = numFromAmount((c as any).amountOwed || '');

    const minOk =
      !appliedFilters.amountMin ||
      amountValue >= Number(appliedFilters.amountMin);

    const maxOk =
      !appliedFilters.amountMax ||
      amountValue <= Number(appliedFilters.amountMax);

    return (
      statusOk && providerOk && serviceTypeOk && locationOk && minOk && maxOk
    );
  });
};

export const applySortToQueriedClaims = (
  queriedClaims: ClaimResponse[],
  selectedSortField: SortField | ''
): ClaimResponse[] => {
  if (!selectedSortField) return queriedClaims;

  const dateCompareDesc = (a: any, b: any): number => {
    const da = new Date(a?.created || '').getTime();
    const db = new Date(b?.created || '').getTime();
    const va = Number.isNaN(da) ? -Infinity : da;
    const vb = Number.isNaN(db) ? -Infinity : db;
    return vb - va;
  };

  const toNum = (val: string) => {
    const match = /\d+(?:\.\d+)?/.exec(val || '');
    return Number.parseFloat(match?.[0] || '0');
  };

  const sorters: Record<string, (a: any, b: any) => number> = {
    providerName: (a, b) =>
      getNormalizedClaimProvider(a).localeCompare(getNormalizedClaimProvider(b)),

    dateOfService: dateCompareDesc,

    status: (a, b) => (a.status || '').localeCompare(b.status || ''),

    amountOwed: (a, b) => toNum(a.amountOwed || '') - toNum(b.amountOwed || ''),

    location: (a, b) => getNormalizedClaimLocation(a).localeCompare(getNormalizedClaimLocation(b)),

    serviceType: (a, b) => {
      const aPart = getServiceTypeParts(a)[0] || '';
      const bPart = getServiceTypeParts(b)[0] || '';
      return aPart.localeCompare(bPart);
    },
  };

  const sorter = sorters[selectedSortField as string];
  if (!sorter) return queriedClaims;

  return [...queriedClaims].sort(sorter);
};
